import React from "react";
import StatisticsCard from "../common/StatisticsCard";
import {IntakesStatistics} from "../../models/Statistics/IntakesStatistics";
import { CHART_COLORS } from "../../constants/ChartColors";

interface IntakesStatisticsCardProps {
  statistics: IntakesStatistics;
  displayOpenIntakeStatistics: boolean;
  displayClosedIntakeStatistics: boolean;
}

const IntakesStatisticsCard: React.FC<IntakesStatisticsCardProps> = ({
  statistics,
  displayOpenIntakeStatistics,
  displayClosedIntakeStatistics
}) => {
  let numberOfIntakes : number = 0;
  if (displayOpenIntakeStatistics) numberOfIntakes = numberOfIntakes + statistics.numberOfOpenIntakes;
  if (displayClosedIntakeStatistics) numberOfIntakes = numberOfIntakes + statistics.numberOfClosedIntakes;

  let intakesMigrated : number = 0;
  if (displayOpenIntakeStatistics) intakesMigrated = intakesMigrated + statistics.openIntakesMigrated;
  if (displayClosedIntakeStatistics) intakesMigrated = intakesMigrated + statistics.closedIntakesMigrated;

  let intakesInProgress : number = 0;
  if (displayOpenIntakeStatistics) intakesInProgress = intakesInProgress + statistics.openIntakesInProgress;
  if (displayClosedIntakeStatistics) intakesInProgress = intakesInProgress + statistics.closedIntakesInProgress;

  let intakesInterrupted : number = 0;
  if (displayOpenIntakeStatistics) intakesInterrupted = intakesInterrupted + statistics.openIntakesInterrupted;
  if (displayClosedIntakeStatistics) intakesInterrupted = intakesInterrupted + statistics.closedIntakesInterrupted;

  let intakesWithFlaggedStatus : number = 0;
  if (displayOpenIntakeStatistics) intakesWithFlaggedStatus = intakesWithFlaggedStatus + statistics.openIntakesFlagged;
  if (displayClosedIntakeStatistics) intakesWithFlaggedStatus = intakesWithFlaggedStatus + statistics.closedIntakesFlagged;

  let intakesResolved : number = 0;
  if (displayOpenIntakeStatistics) intakesResolved = intakesResolved + statistics.openIntakesResolved;
  if (displayClosedIntakeStatistics) intakesResolved = intakesResolved + statistics.closedIntakesResolved;

  let intakesAcknowledged : number = 0;
  if (displayOpenIntakeStatistics) intakesAcknowledged = intakesAcknowledged + statistics.openIntakesAcknowledged;
  if (displayClosedIntakeStatistics) intakesAcknowledged = intakesAcknowledged + statistics.closedIntakesAcknowledged;

  const intakesNotStarted = numberOfIntakes - (intakesMigrated + intakesInProgress + intakesInterrupted + intakesWithFlaggedStatus);
  const intakesFlagged = intakesWithFlaggedStatus - (intakesAcknowledged + intakesResolved);

  const intakeChartData = [
    {
      name: "Migrated",
      value: intakesMigrated,
      color: CHART_COLORS.migrated,
    },
    {
      name: "In Progress",
      value: intakesInProgress,
      color: CHART_COLORS.inProgress,
    },
    {
      name: "Interrupted",
      value: intakesInterrupted,
      color: CHART_COLORS.interrupted,
    },
    {
      name: "Not Started",
      value: intakesNotStarted,
      color: CHART_COLORS.notStarted,
    },
    {
      name: "Flagged",
      value: intakesFlagged,
      color: CHART_COLORS.flagged,
    },
    {
      name: "Allowed",
      value: intakesResolved,
      color: CHART_COLORS.allowed,
    },
    {
      name: "Acknowledged",
      value: intakesAcknowledged,
      color: CHART_COLORS.acknowledged,
    },
  ];

  let totalNumberOfFiles : number = 0;
  if (displayOpenIntakeStatistics) totalNumberOfFiles = totalNumberOfFiles + statistics.totalNumberOfOpenIntakeFiles;
  if (displayClosedIntakeStatistics) totalNumberOfFiles = totalNumberOfFiles + statistics.totalNumberOfClosedIntakeFiles;

  let filesMigrated : number = 0;
  if (displayOpenIntakeStatistics) filesMigrated = filesMigrated + statistics.openIntakeFilesMigrated;
  if (displayClosedIntakeStatistics) filesMigrated = filesMigrated + statistics.closedIntakeFilesMigrated;

  let filesNotFound : number = 0;
  if (displayOpenIntakeStatistics) filesNotFound = filesNotFound + statistics.openIntakeFilesNotFound;
  if (displayClosedIntakeStatistics) filesNotFound = filesNotFound + statistics.closedIntakeFilesNotFound;

  let filesNotFoundMarkedForRetry : number = 0;
  if (displayOpenIntakeStatistics) filesNotFoundMarkedForRetry = filesNotFoundMarkedForRetry + statistics.openIntakeFilesNotFoundMarkedForRetry;
  if (displayClosedIntakeStatistics) filesNotFoundMarkedForRetry = filesNotFoundMarkedForRetry + statistics.closedIntakeFilesNotFoundMarkedForRetry;

  let filesInterruptedInitial : number = 0;
  if (displayOpenIntakeStatistics) filesInterruptedInitial = filesInterruptedInitial + statistics.openIntakeFilesInterruptedInitial;
  if (displayClosedIntakeStatistics) filesInterruptedInitial = filesInterruptedInitial + statistics.closedIntakeFilesInterruptedInitial;

  let filesInterruptedDelta : number = 0;
  if (displayOpenIntakeStatistics) filesInterruptedDelta = filesInterruptedDelta + statistics.openIntakeFilesInterruptedDelta;
  if (displayClosedIntakeStatistics) filesInterruptedDelta = filesInterruptedDelta + statistics.closedIntakeFilesInterruptedDelta;

  let filesModified : number = 0;
  if (displayOpenIntakeStatistics) filesModified = filesModified + statistics.openIntakeFilesModified;
  if (displayClosedIntakeStatistics) filesModified = filesModified + statistics.closedIntakeFilesModified;

  let filesFlagged : number = 0;
  if (displayOpenIntakeStatistics) filesFlagged = filesFlagged + statistics.openIntakeFilesFlagged;
  if (displayClosedIntakeStatistics) filesFlagged = filesFlagged + statistics.closedIntakeFilesFlagged;

  let filesBlockedByConflict : number = 0;
  if (displayOpenIntakeStatistics) filesBlockedByConflict = filesBlockedByConflict + statistics.intakeFilesInOpenIntakesUnresolvedConflictCount;
  if (displayClosedIntakeStatistics) filesBlockedByConflict = filesBlockedByConflict + statistics.intakeFilesInClosedIntakesUnresolvedConflictCount;

  let filesAcknowledgedConflict : number = 0;
  if (displayOpenIntakeStatistics) filesAcknowledgedConflict = filesAcknowledgedConflict + statistics.intakeFilesInOpenIntakesAcknowledgedConflictCount;
  if (displayClosedIntakeStatistics) filesAcknowledgedConflict = filesAcknowledgedConflict + statistics.intakeFilesInClosedIntakesAcknowledgedConflictCount;

  // Conflicted files are blocked from migration, so they get their own chart buckets rather than the "Not Started" remainder.
  const filesNotStarted = Math.max(0, totalNumberOfFiles - (filesMigrated + filesInterruptedInitial + filesInterruptedDelta + filesModified + filesNotFound + filesNotFoundMarkedForRetry + filesFlagged + filesBlockedByConflict + filesAcknowledgedConflict));

  const filesChartData = [
    {
      name: "Migrated",
      value: filesMigrated,
      color: CHART_COLORS.migrated,
    },
    {
      name: "Not Found",
      value: filesNotFound,
      color: CHART_COLORS.inProgress,
    },
    {
      name: "Not Found (Marked For Retry)",
      value: filesNotFoundMarkedForRetry,
      color: CHART_COLORS.acknowledged,
    },
    {
      name: "Interrupted Initial",
      value: filesInterruptedInitial,
      color: CHART_COLORS.interrupted,
    },
    {
      name: "Interrupted Delta",
      value: filesInterruptedDelta,
      color: CHART_COLORS.interruptedDelta,
    },
    {
      name: "Modified",
      value: filesModified,
      color: CHART_COLORS.modified,
    },
    {
      name: "Not Started",
      value: filesNotStarted,
      color: CHART_COLORS.notStarted,
    },
    {
      name: "Blocked by conflict",
      value: filesBlockedByConflict,
      color: CHART_COLORS.blockedByConflict,
    },
    {
      name: "Acknowledged conflict",
      value: filesAcknowledgedConflict,
      color: CHART_COLORS.acknowledgedConflict,
    },
    {
      name: "Flagged",
      value: filesFlagged,
      color: CHART_COLORS.flagged,
    },
  ];

  let dicomFilesFlagged : number = 0;
  if (displayOpenIntakeStatistics) dicomFilesFlagged = dicomFilesFlagged + statistics.dicomOpenIntakeFilesFlagged;
  if (displayClosedIntakeStatistics) dicomFilesFlagged = dicomFilesFlagged + statistics.dicomClosedIntakeFilesFlagged;

  let msgFilesFlagged : number = 0;
  if (displayOpenIntakeStatistics) msgFilesFlagged = msgFilesFlagged + statistics.msgOpenIntakeFilesFlagged;
  if (displayClosedIntakeStatistics) msgFilesFlagged = msgFilesFlagged + statistics.msgClosedIntakeFilesFlagged;

  let filesInIntakesWithOver10KFiles : number = 0;
  if (displayOpenIntakeStatistics) filesInIntakesWithOver10KFiles = filesInIntakesWithOver10KFiles + statistics.intakeFilesInOpenIntakesWithOver10KFiles;
  if (displayClosedIntakeStatistics) filesInIntakesWithOver10KFiles = filesInIntakesWithOver10KFiles + statistics.intakeFilesInClosedIntakesWithOver10KFiles;

  let filesInIntakesBlockedByUser : number = 0;
  if (displayOpenIntakeStatistics) filesInIntakesBlockedByUser = filesInIntakesBlockedByUser + statistics.intakeFilesInOpenIntakesBlockedByUser;
  if (displayClosedIntakeStatistics) filesInIntakesBlockedByUser = filesInIntakesBlockedByUser + statistics.intakeFilesInClosedIntakesBlockedByUser;

  let filesMovedToFirmDocuments : number = 0;
  if (displayOpenIntakeStatistics) filesMovedToFirmDocuments = filesMovedToFirmDocuments + statistics.openIntakeFilesMovedToFirmDocuments;
  if (displayClosedIntakeStatistics) filesMovedToFirmDocuments = filesMovedToFirmDocuments + statistics.closedIntakeFilesMovedToFirmDocuments;

  const flagsChartData = [
    {
      name: "DICOM",
      value: dicomFilesFlagged,
      color: CHART_COLORS.dicom,
    },
    {
      name: "Flagged Extensions",
      value: msgFilesFlagged,
      color: CHART_COLORS.flaggedExtensions,
    },
    {
      name: "Intakes >10K",
      value: filesInIntakesWithOver10KFiles,
      color: CHART_COLORS.over10k,
    },
    {
      name: "Blocked by user",
      value: filesInIntakesBlockedByUser,
      color: CHART_COLORS.blockedByUser,
    },
    {
      name: "Moved To Firm Documents",
      value: filesMovedToFirmDocuments,
      color: CHART_COLORS.movedToFirmDocs,
    }
  ];

  let totalFilesSizeInGb : number = 0;
  if (displayOpenIntakeStatistics) totalFilesSizeInGb = totalFilesSizeInGb + statistics.totalOpenIntakeFilesSizeInGb;
  if (displayClosedIntakeStatistics) totalFilesSizeInGb = totalFilesSizeInGb + statistics.totalClosedIntakeFilesSizeInGb;

  let totalMigratedFilesSizeInGb : number = 0;
  if (displayOpenIntakeStatistics) totalMigratedFilesSizeInGb = totalMigratedFilesSizeInGb + statistics.totalMigratedOpenIntakeFilesSizeInGb;
  if (displayClosedIntakeStatistics) totalMigratedFilesSizeInGb = totalMigratedFilesSizeInGb + statistics.totalMigratedClosedIntakeFilesSizeInGb;

  let totalFilesSizeInMb : number = 0;
  if (displayOpenIntakeStatistics) totalFilesSizeInMb = totalFilesSizeInMb + statistics.totalOpenIntakeFilesSizeInMb;
  if (displayClosedIntakeStatistics) totalFilesSizeInMb = totalFilesSizeInMb + statistics.totalClosedIntakeFilesSizeInMb;

  let totalMigratedFilesSizeInMb : number = 0;
  if (displayOpenIntakeStatistics) totalMigratedFilesSizeInMb = totalMigratedFilesSizeInMb + statistics.totalMigratedOpenIntakeFilesSizeInMb;
  if (displayClosedIntakeStatistics) totalMigratedFilesSizeInMb = totalMigratedFilesSizeInMb + statistics.totalMigratedClosedIntakeFilesSizeInMb;

  let filesMovedToFirmDocumentsSizeInGb : number = 0;
  if (displayOpenIntakeStatistics) filesMovedToFirmDocumentsSizeInGb = filesMovedToFirmDocumentsSizeInGb + statistics.openIntakeFilesMovedToFirmDocumentsSizeInGb;
  if (displayClosedIntakeStatistics) filesMovedToFirmDocumentsSizeInGb = filesMovedToFirmDocumentsSizeInGb + statistics.closedIntakeFilesMovedToFirmDocumentsSizeInGb;

  let filesMovedToFirmDocumentsSizeInMb : number = 0;
  if (displayOpenIntakeStatistics) filesMovedToFirmDocumentsSizeInMb = filesMovedToFirmDocumentsSizeInMb + statistics.openIntakeFilesMovedToFirmDocumentsSizeInMb;
  if (displayClosedIntakeStatistics) filesMovedToFirmDocumentsSizeInMb = filesMovedToFirmDocumentsSizeInMb + statistics.closedIntakeFilesMovedToFirmDocumentsSizeInMb;

  let notResolvedFlaggedFilesSizeInGb : number = 0;
  if (displayOpenIntakeStatistics) notResolvedFlaggedFilesSizeInGb = notResolvedFlaggedFilesSizeInGb + statistics.notResolvedFlaggedOpenIntakeFilesSizeInGb;
  if (displayClosedIntakeStatistics) notResolvedFlaggedFilesSizeInGb = notResolvedFlaggedFilesSizeInGb + statistics.notResolvedFlaggedClosedIntakeFilesSizeInGb;

  let notResolvedFlaggedFilesSizeInMb : number = 0;
  if (displayOpenIntakeStatistics) notResolvedFlaggedFilesSizeInMb = notResolvedFlaggedFilesSizeInMb + statistics.notResolvedFlaggedOpenIntakeFilesSizeInMb;
  if (displayClosedIntakeStatistics) notResolvedFlaggedFilesSizeInMb = notResolvedFlaggedFilesSizeInMb + statistics.notResolvedFlaggedClosedIntakeFilesSizeInMb;

  let notFoundSizeInGb : number = 0;
  if (displayOpenIntakeStatistics) notFoundSizeInGb += statistics.notFoundOpenIntakeFilesSizeInGb || 0;
  if (displayClosedIntakeStatistics) notFoundSizeInGb += statistics.notFoundClosedIntakeFilesSizeInGb || 0;

  let notFoundSizeInMb : number = 0;
  if (displayOpenIntakeStatistics) notFoundSizeInMb += statistics.notFoundOpenIntakeFilesSizeInMb || 0;
  if (displayClosedIntakeStatistics) notFoundSizeInMb += statistics.notFoundClosedIntakeFilesSizeInMb || 0;

  let interruptedSizeInGb : number = 0;
  if (displayOpenIntakeStatistics) interruptedSizeInGb += (statistics.interruptedInitialOpenIntakeFilesSizeInGb || 0) + (statistics.interruptedDeltaOpenIntakeFilesSizeInGb || 0);
  if (displayClosedIntakeStatistics) interruptedSizeInGb += (statistics.interruptedInitialClosedIntakeFilesSizeInGb || 0) + (statistics.interruptedDeltaClosedIntakeFilesSizeInGb || 0);

  let interruptedSizeInMb : number = 0;
  if (displayOpenIntakeStatistics) interruptedSizeInMb += (statistics.interruptedInitialOpenIntakeFilesSizeInMb || 0) + (statistics.interruptedDeltaOpenIntakeFilesSizeInMb || 0);
  if (displayClosedIntakeStatistics) interruptedSizeInMb += (statistics.interruptedInitialClosedIntakeFilesSizeInMb || 0) + (statistics.interruptedDeltaClosedIntakeFilesSizeInMb || 0);

  return (
    <StatisticsCard
      migrationChartData={{ data: intakeChartData, label: "Intakes" }}
      filesChartData={{ data: filesChartData, label: "Files" }}
      fileFlagsChartData={{ data: flagsChartData, label: "Flags"}}
      title={"Intakes Statistics"}
      totalFilesSizeInGb={totalFilesSizeInGb}
      totalMigratedFilesSizeInGb={totalMigratedFilesSizeInGb}
      totalFilesSizeInMb={totalFilesSizeInMb}
      totalMigratedFilesSizeInMb={totalMigratedFilesSizeInMb}
      totalFilesMovedToFirmDocsSizeInGb={filesMovedToFirmDocumentsSizeInGb}
      totalFilesMovedToFirmDocsSizeInMb={filesMovedToFirmDocumentsSizeInMb}
      notResolvedFlaggedFilesSizeInGb={notResolvedFlaggedFilesSizeInGb}
      notResolvedFlaggedFilesSizeInMb={notResolvedFlaggedFilesSizeInMb}
      notFoundFilesSizeInGb={notFoundSizeInGb}
      notFoundFilesSizeInMb={notFoundSizeInMb}
      interruptedFilesSizeInGb={interruptedSizeInGb}
      interruptedFilesSizeInMb={interruptedSizeInMb}
      showFlaggedSizeInfo={true}
    />
  );
};

export default IntakesStatisticsCard;
